import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiClientError } from '../../../../api/httpClient';
import { versionKeys } from './useVersionQueries';
import { generationKeys } from './useGenerationQueries';
import { getVersionState, requestVersionDeletion } from './versionApi';
import { useDeletionJob } from './useDeletionJob';
import { canDeleteVersion } from './versionTypes';
import { acquireDeletionSubmit, createDeletionRequest, releaseDeletionSubmit, saveDeletionRequest, useDeletionRequest } from './deletionRequestStore';

export function useDeleteVersion(version: string) {
  const client = useQueryClient();
  const stored = useDeletionRequest();
  const [storageFailed, setStorageFailed] = useState(false);
  const [checkError, setCheckError] = useState<unknown>(null);
  const [checking, setChecking] = useState(false);
  const mutation = useMutation({ mutationFn: requestVersionDeletion, retry: false });
  const request = stored.request;
  const status = useDeletionJob(request);
  const job = status.isSuccess ? status.data : undefined;
  const isOwnRequest = request?.version === version;
  const canReplace = !request || job?.status === 'failed' || (!isOwnRequest && job?.status === 'succeeded');
  const isReplay = isOwnRequest && !request?.id;
  const error = checkError ?? mutation.error;
  const rejected = error instanceof ApiClientError;
  const retryUnavailable = rejected && (error.statusCode === 403 || error.statusCode === 503 || error.errors.deletion?.[0] === 'idempotency_conflict');
  const confirm = async (): Promise<void> => {
    if (stored.storageFailed || storageFailed || (!canReplace && !isReplay) || retryUnavailable || !acquireDeletionSubmit()) return;
    setCheckError(null);
    setChecking(true);
    try {
      if (!isReplay || rejected) {
        const state = await client.fetchQuery({ queryKey: versionKeys.state(version), queryFn: ({ signal }) => getVersionState(version, signal), staleTime: 0, retry: false });
        if (!canDeleteVersion(state)) return;
      }
      const intent = isReplay && request ? request : createDeletionRequest(version);
      try { saveDeletionRequest(intent); } catch { setStorageFailed(true); return; }
      const job = await mutation.mutateAsync(intent);
      client.setQueryData(versionKeys.deletion(job.id), job);
      try { saveDeletionRequest({ ...intent, id: job.id }); } catch { setStorageFailed(true); }
    } catch (error) {
      setCheckError(error);
    } finally {
      setChecking(false);
      releaseDeletionSubmit();
      void client.invalidateQueries({ queryKey: versionKeys.all });
      void client.invalidateQueries({ queryKey: generationKeys.current });
      void client.invalidateQueries({ queryKey: generationKeys.history });
      void client.invalidateQueries({ queryKey: [...generationKeys.all, 'blockers'] });
    }
  };
  const resetError = (): void => { setCheckError(null); mutation.reset(); };
  return { mutation, request, isOwnRequest, canReplace, isReplay, confirm, error, resetError, rejected, retryUnavailable, pending: checking || mutation.isPending, storageFailed: stored.storageFailed || storageFailed };
}
